import React, { useState, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { MdAdd } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import { Action } from 'helpers';
import { useStyles } from './styles';
import SearchWithFilters from 'components/SearchWithFilters';
import { tableStyles } from 'components/Tables/TableCells/styles';
import { debounce } from 'GeminiViewerComponent/helpers/lodashUtils';
import PopupAction from 'components/Misc/PopupActions/components/PopupAction';
import { makeButtonStyles } from '../../../../GeminiViewerComponent/components/styles';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { ResizableTable } from 'GeminiViewerComponent/components/Tables/ResizableTable';
import { EnhancedTableToolbar } from 'GeminiViewerComponent/components/EnhancedTableToolbar';
import { closePanel, openPanel, setEditObjectId } from 'features/common/formSlice';
import { resizableLabelHeader, resizableLabelCell, resizableActionCell } from 'components';

function ClientTable({ clients, pageInfo, clientPayload, setClientPayload, sortDirection, sortBy, handleSort }) {
    const dispatch = useDispatch();
    const theme = useSelector(selectDefaultTheme);
    const buttonStyles = makeButtonStyles(theme);
    const classes = useStyles();
    const tableClasses = tableStyles();
    const [selected, setSelected] = useState([]);

    const loadedRowsMap = useSelector((state) => state.clients.loadedClientsMap);
    const headerHeight = 40;
    const rowHeight = 60;

    const handleRowClick = async ({ rowData }) => {
        await dispatch(closePanel({ formKey: 'clientForm' }));
        if (rowData?.client_id) {
            await dispatch(
                setEditObjectId({
                    formKey: `clientForm`,
                    object_id: rowData?.client_id,
                })
            );
            dispatch(openPanel({ formKey: `clientForm`, formAction: 'Edit' }));
        }
    };

    const columns = [
        {
            accessorKey: 'client_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            padding: 'none',
            label: '',
            actionCellLevel: 'client',
            targetPopup: 'clientManagement',
            formKey: 'clientForm',
            dispatch: dispatch,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableActionCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'display_name',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            label: 'Name',
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'asset_count',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            label: '# Assets',
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
    ];

    // eslint-disable-next-line no-unused-vars
    function loadMoreRows({ startIndex, stopIndex }) {}

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((searchString) => {
            setClientPayload({
                ...clientPayload,
                searchString: searchString,
            });
        }, 1000),
        [clientPayload]
    );

    const handleCreateNew = () => {
        dispatch(
            openPanel({
                formKey: 'clientForm',
                formAction: 'Create',
                clearEditId: true,
            })
        );
    };

    return (
        <div className={classes.root}>
            <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <Tooltip title="Add New Client">
                    <IconButton onClick={handleCreateNew} size="large" className={buttonStyles.contentAddButton}>
                        <MdAdd className="react-icon" />
                    </IconButton>
                </Tooltip>
                <div style={{ flexGrow: 1 }}></div>
                <SearchWithFilters filter={false} onChange={(evt) => debouncedSearch(evt.target.value)} />
            </div>
            {selected.length > 0 && (
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    toolTipTitle={'Delete'}
                    totalCount={clients?.length}
                >
                    <PopupAction
                        action={Action.DeleteArray}
                        object={{
                            clientIds: selected,
                            setClientIds: setSelected,
                        }}
                        level={'client'}
                        showLabel={false}
                    />
                </EnhancedTableToolbar>
            )}
            <div
                style={{
                    height: `calc(100vh - ${selected.length > 0 ? '345px' : '280px'})`,
                    minWidth: '500px',
                }}
            >
                <ResizableTable
                    initialRows={clients}
                    allRows={clients}
                    totalRowCount={pageInfo.TotalCount}
                    loadedRowsMap={loadedRowsMap}
                    loadMoreRows={loadMoreRows}
                    columns={columns}
                    cellClassName={tableClasses.flexContainer}
                    className={tableClasses.table}
                    rowClassName={tableClasses.flexContainer}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    onRowClick={handleRowClick}
                />
            </div>
        </div>
    );
}

export { ClientTable };
